import { timeAsync } from '@ifixit/helpers';
import { type ScopeContext, SentryError } from '@ifixit/sentry';
import { robustFetch } from '@ifixit/utils/robust-fetch';

// Extend Window interface to include IFixitAPIClient
declare global {
   interface Window {
      IFixitAPIClient: typeof IFixitAPIClient;
   }
}

export * from './client';

export const iFixitAPIRequestHeaderName = 'X-iFixit-API-Request';

export interface ClientOptions {
   origin: string;
   version?: string;
   headers?: HeadersInit;
   disableCacheGets?: boolean;
}

const BypassHeaderName = 'Nextjs-Bypass-Varnish';
export const VarnishBypassHeader = { [BypassHeaderName]: '1' };

export const iFixitAPIRequestHeader = { [iFixitAPIRequestHeaderName]: '1' };

export class IFixitAPIClient {
   static activeRequests: Map<
      IFixitAPIClient,
      {
         url: string;
         method: string;
         headers: Headers;
         endpoint: string;
      }
   > = new Map();
   static completedRequests = 0;

   origin: string;
   version: string;

   headers: HeadersInit;
   disableCacheGets: boolean;

   constructor(readonly options: ClientOptions) {
      this.origin = options.origin;
      this.version = options.version ?? '2.0';
      this.headers = options.headers ?? {};
      this.disableCacheGets = options.disableCacheGets ?? false;
   }

   async getRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'GET',
      });
   }

   async get<Data = unknown>(
      endpoint: string,
      { statName }: { statName: string },
      init?: RequestInit
   ): Promise<Data> {
      const url = new URL(endpoint, this.origin);
      if (this.disableCacheGets) {
         url.searchParams.set('disableCacheGets', '1');
      }
      const newEndpoint = `${url.pathname}${url.search}`;
      return this.getRaw(newEndpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: newEndpoint, init })
      );
   }

   async postRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'POST',
      });
   }

   async post<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.postRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   async putRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'PUT',
      });
   }

   async put<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.putRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   async patchRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'PATCH',
      });
   }

   async patch<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.patchRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   async deleteRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'DELETE',
      });
   }

   async delete<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.deleteRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   async fetch(endpoint: string, statName: string, init?: RequestInit) {
      const pskToken = process.env.NEXT_PUBLIC_DEV_API_AUTH_TOKEN;
      const authHeader: { Authorization?: string } = pskToken
         ? { Authorization: `PSK ${pskToken}` }
         : {};
      const slashEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const url = `${this.origin}/api/${this.version}${slashEndpoint}`;
      const headers = new Headers({
         'User-Agent': 'iFixitNextBackendNinja/1.0',
         ...authHeader,
         ...this.headers,
         ...init?.headers,
         ...iFixitAPIRequestHeader,
      });

      const requestInfo = {
         url,
         method: init?.method || 'GET',
         headers: headers,
         endpoint,
      };

      // Track active request
      IFixitAPIClient.activeRequests.set(this, requestInfo);

      try {
         const method = init?.method?.toUpperCase() ?? 'GET';
         const isIdempotent = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';

         const response = await timeAsync(`ifixit-api.${method.toLowerCase()}.${statName}`, () =>
            robustFetch(url, {
               credentials: 'include',
               ...init,
               headers,
               ...(!isIdempotent && { retry: { maxRetries: 0 } }),
            })
         );

         // Complete request tracking
         IFixitAPIClient.completedRequests++;

         warnIfNotBypassed(headers, response);
         return response;
      } finally {
         // Handle request failure
         IFixitAPIClient.activeRequests.delete(this);
      }
   }

   isJson = (response: Response) => response.headers.get('Content-Type') === 'application/json';

   getJsonFromResponse = async (
      response: Response,
      request?: {
         input: RequestInfo | URL;
         init?: RequestInit;
      }
   ) => {
      if (!response.ok) {
         await this.throwFetchError(response, request);
      }

      if (this.isJson(response)) {
         return response.json();
      }

      return null;
   };

   throwFetchError = async (
      response: Response,
      request?: {
         input: RequestInfo | URL;
         init?: RequestInit;
      }
   ) => {
      const isJson = this.isJson(response);

      const message = response.statusText;

      let body = null;

      try {
         body = isJson ? await response.json() : await response.text();
      } catch {
         body = response.body;
      }
      const body_key = isJson ? 'response_json' : 'response_text';

      const sentryDetails: Partial<ScopeContext> = {
         contexts: {
            request: request,
            response: {
               status: response.status,
               statusText: response.statusText,
            },
            [body_key]: body,
         },
         tags: {
            request_url: response.url,
            response_status_code: response.status.toString(),
            response_status_text: response.statusText,
         },
      };

      throw new FetchError(message, sentryDetails, response, request);
   };
}

// Make IFixitAPIClient available globally for testing purposes
if (typeof window !== 'undefined') {
   window.IFixitAPIClient = IFixitAPIClient;
}

export class FetchError extends SentryError {
   constructor(
      message: string,
      readonly sentryDetails: Partial<ScopeContext>,
      readonly response: Response,
      readonly request?: {
         input: RequestInfo | URL;
         init?: RequestInit;
      }
   ) {
      super(message, sentryDetails);
   }
}

function warnIfNotBypassed(requestHeaders: Headers, response: Response): void {
   const wantsBypass = requestHeaders.has(BypassHeaderName);
   if (!wantsBypass) {
      return;
   }

   const cachedHeaderKey = 'x-debug-cache';

   if (!response.headers.has(cachedHeaderKey)) {
      console.warn(
         'Varnish bypass requested, expected header not found!',
         `Expected header: ${cachedHeaderKey}`
      );
      return;
   }

   const cached = response.headers.get(cachedHeaderKey);
   const responseBypassed = cached === 'MISS';
   if (responseBypassed) {
      return;
   }

   console.warn(
      `Varnish bypass requested, but didn't bypass for ${truncate(response.url, 100)}!`,
      `${cachedHeaderKey}: ${cached}`
   );
}

function truncate(str: string, length: number) {
   return str.length < length ? str : str.substr(0, length - 3) + '...';
}
